import React, { createContext, useContext, useRef, useState } from 'react'
import useAccount from '../hooks/useAccount'
import useRouter from '../hooks/useRouter'
import { VoiceUITool } from '../voice/types'

// The live page-tool registry. It used to live in VoiceAssistantProvider; the
// page agent and the Help pane both need it and the voice provider is going
// away, so it is app-scoped here (Douglas plan T5, D9).

// A page registers its voice tools + context while it is mounted. Only one
// page is mounted at a time, so a single "current registration" slot is enough;
// register sets it, unregister clears it only if the key still matches (so a
// mount-before-unmount navigation ordering doesn't wipe the incoming page).
export interface VoiceRegistration {
  pageKey: string
  uiTools: VoiceUITool[]
  pageContext: () => string
  /** The page's tools as of its latest render. ui_search and ui_call read these (#1534). */
  liveTools?: () => VoiceUITool[]
}

/** What navigateToPage did: navigated, or why not. */
export type NavigateResult = { ok: true; navigated: string; shows: string } | { ok: false; error: string }

/**
 * Navigate to a NAV_PAGES page. 'job' pages need a job; 'customer' pages the
 * customer alone. Shared by Douglas's navigate_to_page and the Help pane's tours.
 */
export function navigateToNavPage(
  page: string,
  ctx: { customer: string; job: string; navigate: (name: string, params: Record<string, string>) => void },
): NavigateResult {
  const def = NAV_PAGES[page]
  if (!def) return { ok: false, error: `unknown page: ${page}` }
  if (def.scope === 'job' && !ctx.job) {
    return { ok: false, error: 'no job is selected; ask the user which job, or pick a customer-level page' }
  }
  ctx.navigate(page, def.scope === 'job' ? { customer: ctx.customer, job: ctx.job } : { customer: ctx.customer })
  return { ok: true, navigated: page, shows: def.label }
}

// Pages Douglas can navigate to. `scope` is the param shape: 'job' routes need
// the current customer + job; 'customer' routes need only the customer. Both
// are read live from the route at call time. Routes keyed on a specific entity
// ID (a dashboard, framework, pipeline, alert, artifact) are intentionally
// excluded — they need a find/lookup step first (a noted follow-on).
export const NAV_PAGES: Record<string, { scope: 'job' | 'customer'; label: string }> = {
  // Customer + job — analysis & data views
  tables: { scope: 'job', label: 'cross-tab tables' },
  dashboard: { scope: 'job', label: 'the project dashboard' },
  variables: { scope: 'job', label: 'the variable tree' },
  files: { scope: 'job', label: 'the file download area' },
  executive: { scope: 'job', label: 'executive reports' },
  autocoder: { scope: 'job', label: 'AutoCoder (AI text classification)' },
  'theme-ai-tasks': { scope: 'job', label: 'Theme AI: the task list (code open text into themes, sentiment, emotion)' },
  themeai: { scope: 'job', label: 'the old Theme AI imports page (AutoCoder pipelines); Theme AI itself is theme-ai-tasks' },
  frameworks: { scope: 'job', label: 'coding frameworks' },
  visualizations: { scope: 'job', label: 'the demo chart slideshow (not DataViz; DataViz is dashboards)' },
  'tiktok-dashboard': { scope: 'job', label: 'the TikTok dashboard' },
  'tiktok-analytics': { scope: 'job', label: 'TikTok analytics' },
  'tiktok-comments': { scope: 'job', label: 'TikTok comments' },
  // Customer level — workspaces & lists
  landing: { scope: 'customer', label: 'the customer landing page' },
  dashboards: { scope: 'customer', label: 'DataViz: the list of dashboards and web apps' },
  alerts: { scope: 'customer', label: 'alerts' },
  agent: { scope: 'customer', label: 'the agent workspace' },
  // Customer level — admin (Managers)
  admin: { scope: 'customer', label: 'admin home' },
  'admin-users': { scope: 'customer', label: 'admin: users' },
  'admin-jobs': { scope: 'customer', label: 'admin: jobs' },
  'admin-context': { scope: 'customer', label: 'admin: organisational context' },
  'admin-settings': { scope: 'customer', label: 'admin: settings' },
  'admin-token-usage': { scope: 'customer', label: 'admin: AI token usage' },
  'admin-azure-costs': { scope: 'customer', label: 'admin: Azure costs' },
  'admin-activity': { scope: 'customer', label: 'admin: user activity' },
  'admin-tasks': { scope: 'customer', label: 'admin: background tasks' },
  'admin-search': { scope: 'customer', label: 'admin: search' },
  'admin-prompts': { scope: 'customer', label: 'admin: prompts' },
  'admin-sessions': { scope: 'customer', label: 'admin: agent sessions' },
  'admin-brand-themes': { scope: 'customer', label: 'admin: brand themes' },
  'admin-notifications': { scope: 'customer', label: 'admin: notifications' },
  'admin-monitoring': { scope: 'customer', label: 'admin: monitoring' },
  'admin-carbon-cache': { scope: 'customer', label: 'admin: tabulation cache' },
  'admin-demos': { scope: 'customer', label: 'admin: demo stories and Reset' },
  'admin-guides': { scope: 'customer', label: 'admin: guided conversations (guides, invitations and responses)' },
  'admin-agent-skills': { scope: 'customer', label: 'admin: skills library' },
  guided: { scope: 'customer', label: 'your guided conversations (invitations from a Manager)' },
  'admin-social-posts': { scope: 'customer', label: 'admin: social posts and their coded comments' },
  // Everyone's own account page: Change Password and Two-step login (#1724).
  'admin-password': { scope: 'customer', label: 'account: change password and two-step login' },
  'admin-ai-models': { scope: 'customer', label: 'admin: AI models in use, where they run, and the Changes list' },
  'admin-ai-usage': { scope: 'customer', label: 'admin: AI usage, every team’s AI and data-source spend for a month or a date range (Managers)' },
}

interface PageToolsContextValue {
  /** The current registration, or null between pages. */
  registration: VoiceRegistration | null
  registerPage: (reg: VoiceRegistration) => void
  unregisterPage: (pageKey: string) => void
  /** Identity of the current registration, e.g. `files:cust:job`; 'app' between pages. */
  pageKey: string
  /** The page-context string. Read live. */
  pageContext: () => string
  /**
   * The mounted page's tools as of its latest render, or none between pages.
   * The Help pane builds its ui_search / ui_call over these (help/helpBridge):
   * only the mounted page's tools exist, so it can never reach another
   * section's.
   */
  livePageTools: () => VoiceUITool[]
  /** Go to a NAV_PAGES page for the current customer and job. */
  navigateToPage: (page: string) => NavigateResult
}

const PageToolsContext = createContext<PageToolsContextValue | null>(null)

export function usePageToolsContext(): PageToolsContextValue {
  const ctx = useContext(PageToolsContext)
  if (!ctx) throw new Error('usePageToolsContext must be used within PageToolsProvider')
  return ctx
}

/** The same context, or null outside a provider (a test tree, a page drawn outside the app shell). */
export function useOptionalPageToolsContext(): PageToolsContextValue | null {
  return useContext(PageToolsContext)
}

export function PageToolsProvider({ children }: React.PropsWithChildren) {
  const account = useAccount()
  const router = useRouter()
  const customer = (router.params?.customer as string) ?? ''
  // The job in the URL, else the one selected in the header. The Agent and
  // DataViz routes carry no :job, and without the fallback every job page
  // (Tables, Files, Variables) was refused from them — while the header showed
  // the job the user had picked. The app's own nav buttons use account.job.
  const job = (router.params?.job as string) || account.job?.name || ''

  const [registration, setRegistration] = useState<VoiceRegistration | null>(null)
  const registrationRef = useRef(registration)
  registrationRef.current = registration

  // Router params are read through a ref so navigateToPage always sees the
  // current customer/job without changing identity.
  const navCtxRef = useRef({ customer, job, navigate: router.navigate })
  navCtxRef.current = { customer, job, navigate: router.navigate }

  const livePageTools = () => {
    const reg = registrationRef.current
    return reg?.liveTools?.() ?? reg?.uiTools ?? []
  }

  const value: PageToolsContextValue = {
    registration,
    registerPage: (reg) => setRegistration(reg),
    unregisterPage: (key) => setRegistration((cur) => (cur && cur.pageKey === key ? null : cur)),
    pageKey: registration?.pageKey ?? 'app',
    pageContext: registration?.pageContext ?? (() => 'No page-specific tools are available on the current view.'),
    livePageTools,
    navigateToPage: (page) => navigateToNavPage(page, navCtxRef.current),
  }

  return <PageToolsContext.Provider value={value}>{children}</PageToolsContext.Provider>
}
